import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {ExtendedLink} from '@github-ui/react-router'
import {handleRepositoryDataError} from '@github-ui/relay-error-helpers'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {
  RepositoryMilestonesInternal,
  type RepositoryMilestonesInternalFragment,
} from '@github-ui/repository-milestone/RepositoryMilestones'
import {Button} from '@primer/react'
import {useEffect} from 'react'
import {type EntryPointComponent, graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {renderIssuesSidebar} from '../components/sidebar/render-issues-sidebar'
import {useSetCurrentViewId} from '../contexts/QueryContext'
import {useEntryPointsLoader} from '../hooks/use-entrypoint-loaders'
import {RepositoryMilestoneNewPage, RepositoryMilestonePage} from '../lazy-routes'
import {preloadWhenIdle} from '../utils/preload-when-idle'
import type {RepositoryMilestoneIndexPageContentInternal$key} from './__generated__/RepositoryMilestoneIndexPageContentInternal.graphql'
import type {RepositoryMilestoneIndexPageQuery} from './__generated__/RepositoryMilestoneIndexPageQuery.graphql'
import {AnalyticsWrapper} from './AnalyticsWrapper'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query RepositoryMilestoneIndexPageQuery(
    $name: String!
    $owner: String!
    $state: MilestoneState!
    # first/skip are optional with defaults so SSR still works before the server
    # is updated to supply them (a required arg would break the current SSR).
    $first: Int = 50
    $skip: Int = 0
    $orderField: MilestoneOrderField = CREATED_AT
    $orderDirection: OrderDirection = DESC
  ) {
    repository(owner: $owner, name: $name) @catch(to: RESULT) {
      id @required(action: THROW)
      ...RepositoryMilestoneIndexPageContentInternal
        @arguments(state: $state, first: $first, skip: $skip, orderField: $orderField, orderDirection: $orderDirection)
    }
  }
`

export const RepositoryMilestoneIndexPage: EntryPointComponent<
  {pageQuery: RepositoryMilestoneIndexPageQuery},
  Record<string, never>
> = ({queries: {pageQuery}}) => {
  const {queryRef} = useEntryPointsLoader(pageQuery, PageQuery)
  if (!queryRef) return null

  return (
    <AnalyticsWrapper category="Repository Milestone Index">
      <RepositoryMilestoneIndexContent pageQueryRef={queryRef} />
    </AnalyticsWrapper>
  )
}

function RepositoryMilestoneIndexContent({
  pageQueryRef,
}: {
  pageQueryRef: PreloadedQuery<RepositoryMilestoneIndexPageQuery>
}) {
  const pageData = usePreloadedQuery<RepositoryMilestoneIndexPageQuery>(PageQuery, pageQueryRef)

  useEffect(() => {
    return preloadWhenIdle(() => {
      RepositoryMilestonePage.preload()
      RepositoryMilestoneNewPage.preload()
    })
  }, [])

  const setCurrentViewId = useSetCurrentViewId()
  useEffect(() => {
    setCurrentViewId(VIEW_IDS.repository)
  }, [pageQueryRef, setCurrentViewId])

  const repositoryData = pageData?.repository

  const okResult = handleRepositoryDataError(repositoryData)
  if (!okResult || !okResult.value) {
    return null
  }

  return <RepositoryMilestoneIndexPageContentInternal repository={okResult.value} />
}

function RepositoryMilestoneIndexPageContentInternal({
  repository,
}: {
  repository: RepositoryMilestoneIndexPageContentInternal$key
}) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment RepositoryMilestoneIndexPageContentInternal on Repository
      @argumentDefinitions(
        state: {type: "MilestoneState!"}
        first: {type: "Int!"}
        skip: {type: "Int", defaultValue: 0}
        orderField: {type: "MilestoneOrderField", defaultValue: CREATED_AT}
        orderDirection: {type: "OrderDirection", defaultValue: DESC}
      ) {
        viewerCanPush
        nameWithOwner
        milestones(
          first: $first
          skip: $skip
          states: [$state]
          orderBy: {field: $orderField, direction: $orderDirection}
        ) {
          totalCount
          edges {
            node {
              closed
              closedIssueCount
              description
              dueOn
              id
              openIssueCount
              progressPercentage
              repository {
                id
                viewerCanPush
              }
              state
              title
              url
            }
          }
        }
        open: milestones(first: 0, states: OPEN) {
          totalCount
        }
        closed: milestones(first: 0, states: CLOSED) {
          totalCount
        }
      }
    `,
    repository,
  )

  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()
  const headerActions =
    sidebarLayoutEnabled && data.viewerCanPush ? (
      // ExtendedLink hard-navigates to hand off to the Data Router app when /milestones/new isn't
      // registered here (flag on); soft-navigates otherwise. Keep in sync with MilestonesActions.
      <Button
        as={ExtendedLink}
        to={`/${data.nameWithOwner}/milestones/new`}
        variant="primary"
        data-testid="new-milestone-button"
      >
        New milestone
      </Button>
    ) : undefined

  return (
    <RepoIssuesPageLayout
      dialogTitle="Milestones"
      title={sidebarLayoutEnabled ? 'Milestones' : undefined}
      actions={headerActions}
      renderSidebar={renderIssuesSidebar}
    >
      {/* RELAY-CLEANUP(RepositoryMilestoneIndexPageContentInternal): boundary cast from the still-Relay parent
          fragment. Remove when RepositoryMilestoneIndexPageContentInternal leaves Relay. */}
      <RepositoryMilestonesInternal repository={data as RepositoryMilestonesInternalFragment} />
    </RepoIssuesPageLayout>
  )
}
